import type { ProtocolResponse } from "@/mocks/protocolResponses";
import Badge from "@/components/base/Badge";

interface Props {
  data: ProtocolResponse;
}

const priorityConfig = {
  verde: {
    color: "bg-accent-100 text-accent-800 border-accent-300",
    icon: "ri-check-double-line",
    label: "Actuación Ordinaria",
    dot: "bg-accent-500",
  },
  amarillo: {
    color: "bg-amber-100 text-amber-800 border-amber-300",
    icon: "ri-alert-line",
    label: "Requiere Seguimiento",
    dot: "bg-amber-500",
  },
  rojo: {
    color: "bg-red-100 text-red-800 border-red-300",
    icon: "ri-shield-flash-line",
    label: "Actuación Inmediata",
    dot: "bg-red-500",
  },
  no_determinado: {
    color: "bg-background-200 text-foreground-600 border-background-300",
    icon: "ri-question-line",
    label: "No Determinado",
    dot: "bg-foreground-400",
  },
};

export default function ProtocolSummaryCard({ data }: Props) {
  const priority = priorityConfig[data.nivel_prioridad];

  return (
    <div className={`rounded-xl border-2 ${priority.color.split(" ")[2]} bg-white overflow-hidden`}>
      {/* Priority bar */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className={`w-10 h-10 rounded-full ${priority.dot.replace("bg-", "bg-")} flex items-center justify-center flex-shrink-0`}>
          <i className={`${priority.icon} text-white text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${priority.color}`}>
              <span className={`w-2 h-2 rounded-full ${priority.dot}`} />
              {priority.label}
            </span>
            {data.protocolo_aplicable && (
              <Badge variant="primary" size="sm">
                <i className="ri-file-text-line text-xs" />
                {data.protocolo_aplicable.length > 45
                  ? data.protocolo_aplicable.slice(0, 45) + "..."
                  : data.protocolo_aplicable}
              </Badge>
            )}
          </div>
          <h3 className="text-base font-semibold text-foreground-900 mt-2 leading-snug">
            {data.situacion}
          </h3>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 px-5 pb-4">
        <div className="flex items-center gap-2 text-sm">
          <i className="ri-user-star-line text-foreground-400 text-base" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-400">Responsable inicial</p>
            <p className="text-sm font-medium text-foreground-800 truncate">
              {data.pasos.length > 0 ? data.pasos[0].responsable : "No especificado"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <i className="ri-sticky-note-line text-foreground-400 text-base" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-400">Documento base</p>
            <p className="text-sm font-medium text-foreground-800 truncate">
              {data.documentos_fuente.length > 0 ? data.documentos_fuente[0] : "No hay fuente"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <i className="ri-list-check-2 text-foreground-400 text-base" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-400">Pasos del protocolo</p>
            <p className="text-sm font-medium text-foreground-800">
              {data.pasos.length} paso{data.pasos.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <i className="ri-group-line text-foreground-400 text-base" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-400">Responsables involucrados</p>
            <p className="text-sm font-medium text-foreground-800">
              {data.responsables.length} rol{data.responsables.length !== 1 ? "es" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      {data.advertencia_general && data.pasos.length > 0 && (
        <div className="flex items-start gap-2 px-5 py-3 bg-amber-50 border-t border-amber-200">
          <i className="ri-information-line text-amber-600 text-sm flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">{data.advertencia_general}</p>
        </div>
      )}
    </div>
  );
}